import React from 'react';
import differenceInDays from 'date-fns/differenceInDays';
import CampaignLink from '@/components/common/CampaignLink';
import ConstituentLink from '@/components/common/ConstituentLink';
import EmployeeLink from '@/components/common/EmployeeLink';
import LabelChips from '@/components/common/LabelChips';
import Loader from '@/components/common/Loader';
import EntityContentType from '@/constants/EntityContentType';
import { CUSTOM_DATE_FORMAT } from '@/lib/constants/common';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import {
  OpportunityContributionSummary,
  OpportunityMenu,
} from '@/modules/Opportunities';
import type Opportunity from '@/types/api/Opportunity';
import { formatCurrencyWithDecimals } from '@/utils/currency';
import { dateStringToDate, formatDate } from '@/utils/dates';
import useOpportunityData from '../hooks/useOpportunityData';
import useEntityAttachments from '../lib/useEntityAttachments';
import ContentItem from './ContentItem';
import EntityDetailsContentBody from './EntityDetailsContentBody';
import EntityDetailsContentDetails from './EntityDetailsContentDetails';
import EntityDetailsFileUploader from './EntityDetailsFileUploader';
import EntityDetailsFooter from './EntityDetailsFooter';
import EntityDetailsHeader from './EntityDetailsHeader';

const NOW = new Date();

export default function OpportunityEntityDetails({
  onClose,
  opportunityId,
}: {
  onClose: () => void;
  opportunityId: string;
}) {
  const { campaign, constituent, hasData, labels, opportunity } =
    useOpportunityData(opportunityId);
  const { handleUpload } = useEntityAttachments({
    entityId: opportunityId,
    entityType: EntityContentType.OPPORTUNITY,
  });

  if (!hasData) {
    return <Loader className="mb-3" isCentered />;
  }

  const {
    askAmount,
    attachments,
    confidenceLevel,
    created,
    createdBy,
    expectedAmount,
    expectedCloseDate,
    factoredAmount,
    notes,
    owner,
    opportunityStage,
    updated,
    updatedBy,
  } = opportunity as Opportunity;

  return (
    <div>
      <EntityDetailsHeader onClose={onClose}>
        {translate('OPPORTUNITIES_DETAILS_HEADER')}
      </EntityDetailsHeader>

      <OpportunityContributionSummary opportunityId={opportunityId} />

      <EntityDetailsContentBody entityId={opportunityId} hideBorder>
        <EntityDetailsContentDetails>
          {constituent && (
            <ContentItem title={translate('CONSTITUENT')}>
              <ConstituentLink
                constituentId={constituent.constituentId}
                displayName={constituent.displayName}
              />
            </ContentItem>
          )}
          {owner && (
            <ContentItem title={translate('OPPORTUNITIES_OWNER_LABEL')}>
              <EmployeeLink
                userId={owner.userId}
                displayName={owner.displayName}
              />
            </ContentItem>
          )}
          {!!askAmount && (
            <ContentItem title={translate('OPPORTUNITIES_ASK_LABEL')}>
              {formatCurrencyWithDecimals(askAmount, true)}
            </ContentItem>
          )}
          {confidenceLevel && (
            <ContentItem title={translate('OPPORTUNITIES_CONFIDENCE_LABEL')}>
              {confidenceLevel?.name}
            </ContentItem>
          )}

          {!!expectedAmount && (
            <ContentItem title={translate('OPPORTUNITIES_EXPECTED_LABEL')}>
              {formatCurrencyWithDecimals(expectedAmount, true)}
            </ContentItem>
          )}
          {opportunityStage && (
            <ContentItem title={translate('OPPORTUNITIES_STAGE_LABEL')}>
              {opportunityStage.name}
            </ContentItem>
          )}
          {!!factoredAmount && (
            <ContentItem title={translate('OPPORTUNITIES_PROJECTED_LABEL')}>
              {formatCurrencyWithDecimals(factoredAmount, true)}
            </ContentItem>
          )}
          {campaign && (
            <ContentItem title={translate('CAMPAIGN')}>
              <CampaignLink
                campaignDisplayName={campaign.displayName}
                campaignId={campaign.campaignId}
              />
            </ContentItem>
          )}
          {expectedCloseDate && (
            <ContentItem
              title={translate('OPPORTUNITIES_EXPECTED_CLOSE_DATE_LABEL')}
            >
              {formatDate(expectedCloseDate, CUSTOM_DATE_FORMAT)}
            </ContentItem>
          )}
          <ContentItem title={translate('OPPORTUNITIES_DAYS_ACTIVE_LABEL')}>
            {`${differenceInDays(NOW, dateStringToDate(created))}`}
          </ContentItem>
          {notes && (
            <ContentItem
              title={translate('OPPORTUNITIES_NOTES_LABEL')}
              className="col-span-full"
            >
              {notes}
            </ContentItem>
          )}
          {labels && !!labels.length && (
            <ContentItem className="col-span-full">
              <LabelChips labels={labels} />
            </ContentItem>
          )}
        </EntityDetailsContentDetails>
      </EntityDetailsContentBody>

      <EntityDetailsFileUploader
        attachments={attachments}
        onUploadFinished={handleUpload}
      />
      <EntityDetailsFooter
        created={created}
        createdBy={createdBy}
        updated={updated}
        updatedBy={updatedBy}
      >
        <OpportunityMenu
          onArchive={onClose}
          opportunity={opportunity as Opportunity}
        />
      </EntityDetailsFooter>
    </div>
  );
}
